import { makeStyles } from '@fluentui/react-components';

import { statusPalette } from '@/theme/tokens';
import type { DailyUptime } from '@/api/types';

interface UptimeSparklineProps {
  data: readonly DailyUptime[];
  height?: number;
}

const useStyles = makeStyles({
  bars: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '2px',
    height: '36px',
  },
  bar: {
    flex: '1 1 0',
    minWidth: '3px',
    borderRadius: '2px',
    transition: 'opacity 120ms ease',
  },
  // Empty-state placeholder: same 36px box as the bars container so cards
  // with no daily uptime data still take the same vertical space, keeping
  // the `% number` row above it aligned across the row.
  empty: {
    height: '36px',
  },
});

function colorForUptime(pct: number): string {
  if (pct >= 99.9) {
    return statusPalette.healthy;
  }
  if (pct >= 99.0) {
    return statusPalette.degraded;
  }
  return statusPalette.unhealthy;
}

export function UptimeSparkline({ data, height = 36 }: UptimeSparklineProps) {
  const classes = useStyles();
  if (data.length === 0) {
    // Reserve the same vertical space the populated chart would occupy so
    // cards with no data don't shift their `%` row up vs cards that have a
    // 30-day series. `role="img"` + `aria-label` keep screen readers informed.
    return (
      <div
        className={classes.empty}
        style={{ height: `${String(height)}px` }}
        role="img"
        aria-label="No uptime data for the last 30 days"
      />
    );
  }
  return (
    <div className={classes.bars} style={{ height: `${String(height)}px` }} role="img" aria-label="Daily uptime over the last 30 days">
      {data.map((d) => {
        const ratio = Math.max(0, Math.min(1, (d.uptimePct - 95) / 5));
        const heightPx = Math.max(4, Math.round(ratio * height));
        return (
          <span
            key={d.dayStartEpochMs}
            className={classes.bar}
            style={{ height: `${String(heightPx)}px`, backgroundColor: colorForUptime(d.uptimePct) }}
            title={`${new Date(d.dayStartEpochMs).toLocaleDateString()}: ${d.uptimePct.toFixed(2)}%`}
          />
        );
      })}
    </div>
  );
}
